'use client';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Alert, alpha, Link, Typography } from '@mui/material';

import { useT } from '@/hooks';
import { usePolymarketStatus } from '@/services/query';

import { TOP_BANNER_HEIGHT } from '@/constants';

const STATUS_PAGE_URL = 'https://status.polymarket.com';

/**
 * Top banner shown while Polymarket's upstream API is degraded or down, based on
 * the public status page (https://status.polymarket.com). Hidden when everything
 * is operational (indicator === 'none'), or while loading / on fetch error — we
 * never want a transient poll failure to itself raise a false alarm.
 *
 * Severity → colour: minor => warning (amber), major/critical => error (red),
 * matching the warm theme, mirroring GeoBlockBanner / StreakBanner.
 */
export const PolymarketApiStatusBanner = () => {
  const { t } = useT();
  const { data } = usePolymarketStatus();

  // Instatus page.status: 'UP' | 'HASISSUES' | 'UNDERMAINTENANCE' (uppercase).
  const pageStatus = (data?.page?.status || 'UP').toUpperCase();
  const incidents = data?.activeIncidents ?? [];
  const topIncident = incidents[0];
  const underMaintenance = pageStatus === 'UNDERMAINTENANCE';
  const activeMaintenance = underMaintenance ? data?.activeMaintenances?.[0] : undefined;

  // Show when the page is not fully "UP" or there's a live incident.
  const hasIssue = pageStatus !== 'UP' || incidents.length > 0;
  if (!data || !hasIssue) return null;

  // Severity from the worst active incident's impact; maintenance is amber.
  const impact = (topIncident?.impact || '').toUpperCase();
  const severity: 'warning' | 'error' =
    impact === 'MAJOROUTAGE' || impact === 'PARTIALOUTAGE'
      ? 'error'
      : impact === 'DEGRADEDPERFORMANCE' || underMaintenance
        ? 'warning'
        : 'error';

  // Prefer the live incident's title; else the maintenance name; else generic.
  const detail =
    topIncident?.name ||
    activeMaintenance?.name ||
    (underMaintenance ? 'Scheduled maintenance in progress' : 'Service disruption');
  const href = topIncident?.url || activeMaintenance?.url || data.page?.url || STATUS_PAGE_URL;

  const message = underMaintenance
    ? t('polymarketStatus.maintenanceMessage', {
        detail,
        defaultValue: `Porvenix  maintenance in progress: ${detail}. Trading and prices may be delayed.`,
      })
    : t('polymarketStatus.bannerMessage', {
        detail,
        defaultValue: `Porvenix is reporting service issues: ${detail}. Trading and prices may be delayed.`,
      });

  return (
    <Alert
      icon={false}
      severity={severity}
      sx={(theme) => {
        const palette = theme.palette[severity];
        const isDark = theme.palette.mode === 'dark';
        return {
          borderRadius: 0,
          height: TOP_BANNER_HEIGHT,
          px: { xs: 2, sm: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(palette.main, isDark ? 0.12 : 0.08),
          borderBottom: `1px solid ${alpha(palette.main, isDark ? 0.25 : 0.2)}`,
          backdropFilter: 'blur(8px)',
          '& .MuiAlert-message': {
            width: '100%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            padding: 0,
          },
        };
      }}
    >
      <ReportProblemIcon
        fontSize="small"
        sx={(theme) => ({ color: theme.palette[severity].main, flexShrink: 0 })}
      />
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        underline="hover"
        title={message}
        sx={(theme) => ({
          fontWeight: 500,
          fontSize: { xs: '0.75rem', sm: '0.8125rem' },
          color:
            theme.palette.mode === 'dark'
              ? theme.palette[severity].main
              : theme.palette[severity].dark,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        })}
      >
        <Typography component="span" variant="body2" sx={{ font: 'inherit', color: 'inherit' }}>
          {message}
        </Typography>
      </Link>
    </Alert>
  );
};

export default PolymarketApiStatusBanner;
